const PRODUCT_IMG =
  'https://customer-assets.emergentagent.com/job_80fffe0f-7ad4-44ce-aa36-08a4bccc4edb/artifacts/78savfvp_jan.png';

const escargot = {
  slug: 'escargot-commercial',
  name: 'Escargot',
  testid: 'escargot',
  accent: 'blue',
  enquiryOnly: true,
  breadcrumb: { label: 'SMARTBOT — Floor Cleaning Robots', href: '/floor-cleaning-robots' },
  brochureUrl:
    'https://customer-assets-jt897jd0.emergentagent.net/job_582314cb-e0d4-4f3b-95a4-147554a5a708/artifacts/jlzbcudn_Commercial%20Floor%20Cleaning%20Robot_Harpalyk_ESCARGOT.pdf',
  meta: {
    title: 'Escargot — Commercial Floor Cleaning Robot (Sweep + Vacuum + Mop) | Harpalyk India',
    description:
      'Escargot: commercial floor cleaning robot with 3-in-1 sweeping, vacuuming & mopping, 40,000m² smart mapping, UV disinfection, 15L water tank, 500 m²/h efficiency and 4-6 hour runtime. For offices, malls, hotels and large facilities in India.',
    keywords:
      'Escargot, commercial floor cleaning robot, industrial cleaning robot India, autonomous floor scrubber, sweeping robot, commercial robot vacuum, SMARTBOT commercial, Harpalyk Escargot',
    canonical: 'https://harpalyk.com/products/escargot-commercial',
  },
  schema: {
    name: 'Harpalyk Escargot Commercial Floor Cleaning Robot',
    description:
      'Escargot is a commercial-grade autonomous floor cleaning robot with 3-in-1 sweeping, vacuuming and mopping, 40,000m² large-scene mapping, UV disinfection lamp, circulating water filtration, 15L water tank and 500 m²/h cleaning efficiency — built for offices, malls, hotels and large facilities.',
    image: PRODUCT_IMG,
    category: 'Commercial Floor Cleaning Robot',
    price: '2399900',
    ratingValue: '4.8',
    reviewCount: '21',
  },
  hero: {
    gradient: 'from-slate-900 via-blue-900 to-slate-800',
    badge: 'SMARTBOT · Commercial Floor Cleaning Robot',
    tagline: 'Powerful Cleaning. Intelligent Performance.',
    description:
      'A commercial-grade autonomous cleaning robot that sweeps, vacuums and mops in a single pass — mapping up to 40,000m², disinfecting with UV light and managing itself so your facility team doesn\u2019t have to.',
    highlights: [
      { label: '40,000 m² Smart Mapping', icon: 'Maximize2' },
      { label: 'Sweep + Vacuum + Mop', icon: 'Sparkles' },
      { label: 'UV Disinfection Lamp', icon: 'Shield' },
      { label: '500 m²/h Efficiency', icon: 'Zap' },
    ],
    galleryAccent: 'blue',
    galleryBadge: 'Commercial Floor Robot',
  },
  overview: {
    heading: 'Why Escargot?',
    sub: 'Escargot replaces repetitive manual cleaning across large commercial spaces — planning its own routes, cleaning multiple zones, disinfecting as it goes and reporting everything back to your team in real time.',
    checklistTitle: 'Built for a Spotless Facility',
    checklist: [
      'Three effects in one pass — sweeping, vacuuming and mopping simultaneously',
      'Intelligent route planning with bow-shaped wall-following coverage — no omissions',
      'Maps and cleans very large scenes up to 40,000 m² across multiple zones',
      'High-power 11W UV disinfection lamp eliminates bacteria and viruses',
      'Circulating water filtration & disinfection — no secondary pollution, fewer water changes',
      'Automatically returns to charge and resumes exactly where it left off',
      'Cloud platform with remote monitoring, cleaning statistics and maintenance reminders',
    ],
    panelGradient: 'from-blue-50 to-slate-100',
    stats: [
      { value: '40,000 m²', label: 'Scene Mapping' },
      { value: '500 m²/h', label: 'Cleaning Efficiency' },
      { value: '15 L', label: 'Water Tank' },
      { value: '4-6 hrs', label: 'Battery Life' },
      { value: '3-in-1', label: 'Sweep · Vacuum · Mop' },
      { value: '11 W', label: 'UV Disinfection' },
    ],
  },
  features: {
    heading: 'Eight Core Advantages',
    items: [
      { icon: 'Navigation', title: 'Intelligent Planning', description: 'Autonomous cleaning path planning with optimized algorithms for efficient, complete coverage of every zone.' },
      { icon: 'CalendarClock', title: 'Appointment Timing', description: 'Schedule cleaning and disinfection programs in advance — Escargot executes automatically, even after hours.' },
      { icon: 'Shield', title: 'Multiple Collision Avoidance', description: 'Layered obstacle detection and autonomous avoidance keep people, furniture and the robot safe.' },
      { icon: 'Smartphone', title: 'Remote Monitoring', description: 'Monitor robot status, cleaning maps and live progress anytime, anywhere via the cloud service platform.' },
      { icon: 'Maximize2', title: 'Multitask Cleaning', description: 'Multi-task cross-zone cleaning seamlessly handles multiple areas and tasks in one deployment.' },
      { icon: 'RotateCcw', title: 'Automatic Resume', description: 'If suspended or interrupted, Escargot picks up right where it left off for uninterrupted cleaning.' },
      { icon: 'Waves', title: 'Ultrasound Anti-Drop', description: 'Ultrasonic anti-drop sensors detect edges and level changes to prevent falls on stairs and ramps.' },
      { icon: 'Zap', title: 'Automatic Charging', description: 'Returns to its dock on low battery, recharges in 5-6 hours and resumes the schedule autonomously.' },
    ],
  },
  sections: [
    {
      type: 'split',
      label: 'How It Works',
      heading: 'Map. Clean. Report.',
      desc: 'Escargot works in three simple steps: it maps your facility (up to 40,000 m²), cleans every zone with a bow-shaped wall-following path for full coverage, and reports statistics, alerts and maintenance reminders to your team.',
      itemIcon: 'CheckCircle2',
      items: [
        { title: '1. Smart Mapping of Every Zone' },
        { title: '2. 3-in-1 Cleaning on Optimized Routes' },
        { title: '3. Live Reports, Stats & Reminders' },
        { title: 'Auto-Charge & Resume In Between' },
      ],
      panel: {
        gradient: 'from-blue-700 to-slate-800',
        title: 'Full Coverage. No Omissions.',
        desc: 'The bow-shaped wall-following cleaning path ensures thorough, spotless cleaning across corridors, lobbies, halls and open floors — with smart statistics of every square metre cleaned.',
        subClass: 'text-blue-100',
        statClass: 'text-sky-300',
        stats: [
          { value: '40,000', label: 'm² Mapping' },
          { value: '900mm', label: 'Min. Passing Width' },
        ],
      },
    },
    {
      type: 'darkCards',
      label: 'Cleaning Technology',
      heading: 'Three Effects in One',
      sub: 'Sweeping, vacuuming and mopping in a single pass — with hospital-grade hygiene features built in.',
      cards: [
        { icon: 'Sparkles', title: 'Sweep + Vacuum + Mop', desc: 'A high-polymer roller sponge sweeps debris, vacuums dust and mops floors simultaneously — durable and efficient on all commercial floor types.', badge: '3-in-1 Cleaning' },
        { icon: 'Droplets', title: 'Circulating Water Filtration', desc: 'Original water circulation filtration technology cleans and reuses water with disinfection — no secondary pollution and far fewer water changes.', badge: '15L Tank • No Re-Pollution' },
        { icon: 'Shield', title: 'UV Disinfection', desc: 'A high-power 11W UV disinfection lamp eliminates bacteria and viruses as the robot cleans — ideal for hospitals, schools and food areas.', badge: '11W UV Lamp' },
      ],
    },
    {
      type: 'listGroups',
      label: 'Use Cases',
      heading: 'Made for Large Indian Facilities',
      groups: [
        { title: 'Where It Works', items: ['Corporate Offices & IT Parks', 'Shopping Malls & Retail', 'Hotels, Lobbies & Banquets', 'Hospitals, Schools & Airports'] },
        { title: 'Cleaning Modes', items: ['Standard Mode', 'Heavy Mode', 'Water Absorption Mode', 'Custom Mode'] },
        { title: 'Working Modes', items: ['Cycle Automatic Cleaning', 'Timed / Scheduled Tasks', 'Manual Cleaning', 'Disinfection Program'] },
      ],
    },
    {
      type: 'duoPanels',
      bg: 'gray',
      panels: [
        {
          theme: 'blue',
          icon: 'Users',
          title: 'User Authority Management',
          desc: 'Add different users and assign role-based permissions so facility teams can manage the robot safely and transparently every day.',
          listIcon: 'CheckCircle2',
          list: [
            'Multi-user support for centralized control',
            'Role-based permissions and access levels',
            'Safe, traceable and reliable operation',
            'Convenient daily management of robot activities',
            'Intelligent reminders: maintenance, consumables & faults',
          ],
        },
        {
          theme: 'emerald',
          icon: 'BatteryCharging',
          title: 'Strong Endurance, Hands-Free',
          desc: 'Long battery life and fully autonomous charging keep Escargot cleaning shift after shift without supervision.',
          rows: [
            { icon: 'Timer', pre: '', bold: '4-6 hours', post: ' of continuous cleaning per charge' },
            { icon: 'Zap', pre: '', bold: 'Auto-return', post: ' to dock on low battery' },
            { icon: 'RotateCcw', pre: '', bold: 'Auto-resume', post: ' cleaning after charging' },
          ],
        },
      ],
    },
  ],
  specs: {
    defaultTab: 'general',
    tabs: [
      { id: 'general', label: 'General' },
      { id: 'performance', label: 'Performance' },
      { id: 'cleaning', label: 'Cleaning System' },
      { id: 'control', label: 'Control & Power' },
    ],
    data: {
      general: [
        { label: 'Model Name', value: 'Escargot Sweeper Cleaning Robot' },
        { label: 'Application', value: 'Commercial & Institutional Facilities' },
        { label: 'Dimensions (L×W×H)', value: '694 × 500 × 600 mm' },
        { label: 'Whole Machine Weight', value: '50 kg' },
        { label: 'Color', value: 'White' },
        { label: 'Screen Size', value: '7-inch Touch Display' },
        { label: 'Certification', value: 'CE, ROHS' },
      ],
      performance: [
        { label: 'Large Scene Mapping', value: 'Up to 40,000 m²' },
        { label: 'Cleaning Efficiency', value: '500 m²/hour' },
        { label: 'Minimum Passing Width', value: '900 mm' },
        { label: 'Cleaning Route', value: 'Optimized Bow-Shaped Wall-Following Path' },
        { label: 'Battery Life', value: '4-6 Hours' },
        { label: 'Charging Time', value: '5-6 Hours (Automatic)' },
      ],
      cleaning: [
        { label: 'Cleaning Effects', value: 'Sweeping + Vacuuming + Mopping (3-in-1)' },
        { label: 'Roller', value: 'High Polymer Roller Sponge' },
        { label: 'Water Tank Capacity', value: '15 L Large Capacity' },
        { label: 'Water System', value: 'Circulating Filtration & Disinfection' },
        { label: 'UV Disinfection Lamp', value: '11 W High-Power UV' },
        { label: 'Cleaning Modes', value: 'Standard / Heavy / Water Absorption / Custom' },
        { label: 'Working Modes', value: 'Cycle Automatic / Manual' },
      ],
      control: [
        { label: 'Battery', value: '37V / 20Ah Lithium Battery' },
        { label: 'Rated Power', value: '80 W (Whole Machine 130 W)' },
        { label: 'Navigation', value: 'Intelligent Mapping & Path Planning' },
        { label: 'Safety Sensors', value: 'Ultrasound Anti-Drop, Multi-Collision Avoidance' },
        { label: 'Connectivity', value: 'WiFi + Cloud Service Platform' },
        { label: 'Remote Control', value: 'Web Monitoring, Status & Data Tracking' },
        { label: 'User Management', value: 'Multi-User with Role-Based Permissions' },
      ],
    },
  },
  contact: {
    heading: 'Automate Your Facility\u2019s Cleaning',
    sub: 'Get a customized quote, on-site demo and ROI analysis for the Escargot commercial floor cleaning robot.',
    gradient: 'from-blue-700 to-slate-800',
  },
  faqs: [
    {
      question: 'How large an area can the Escargot clean?',
      answer:
        'Escargot maps and manages very large scenes up to 40,000 m² and cleans at an efficiency of 500 m²/hour. With multi-task cross-zone cleaning it can handle multiple areas — corridors, lobbies, halls — in a single deployment, running 4-6 hours per charge.',
    },
    {
      question: 'Does it sweep, vacuum and mop at the same time?',
      answer:
        'Yes. Escargot delivers three effects in one pass — sweeping, vacuuming and mopping — using a durable high-polymer roller sponge that works on all common commercial floor types.',
    },
    {
      question: 'How does the disinfection system work?',
      answer:
        'Escargot combines a high-power 11W UV disinfection lamp that eliminates bacteria and viruses with circulating water filtration & disinfection, so mopping water stays clean with no secondary pollution and fewer water changes from the 15L tank.',
    },
    {
      question: 'Can my facility team monitor and control it remotely?',
      answer:
        'Yes. Through the cloud service platform your team can remotely monitor robot status, view cleaning maps and statistics, schedule timed tasks and receive intelligent reminders for maintenance, consumable replacement and faults. Multi-user authority management assigns different permission levels to different staff.',
    },
    {
      question: 'What happens when the battery runs low?',
      answer:
        'Escargot automatically returns to its charging dock, recharges in 5-6 hours and resumes cleaning exactly where it left off — no manual intervention needed. Ultrasound anti-drop sensors and multi-collision avoidance keep it safe throughout.',
    },
    {
      question: 'How do I get pricing and a demo?',
      answer:
        'Escargot is an enquiry-first commercial product. Use Get a Quote or Book a Demo on this page — our team will arrange an on-site demonstration, customized pricing and an ROI analysis for your facility. Pan-India installation and service support included.',
    },
  ],
  related: [
    { label: 'SMARTBOT — All Floor Robots', href: '/floor-cleaning-robots' },
    { label: 'Industrial Cleaning Robots', href: '/industrial-cleaning-robots' },
    { label: 'LumineX 300', href: '/products/luminex-300' },
    { label: 'HygienX 300', href: '/products/hygienx-300' },
    { label: 'View All Products', href: '/#products' },
  ],
};

export default escargot;
